import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Divider, Typography } from '@mui/material'
import { FlexCol, FlexRow } from '../Flex'
import { marked } from 'marked'
import { ReactNode } from 'react'
import { FaCheck, FaTimes } from 'react-icons/fa'

import { useUser, useUserInfo } from '../../User'
import { addDays, fromNanoseconds } from '../../utils'
import { CustomList } from '../CustomList'
import RedeemRequest from '../Redeem/RedeemRequest'
import isActiveRequest from './isActiveRequest'

interface Props {
  loading?: boolean
  requests?: RedeemRequest[]
}

const BeforeYouWithdraw: React.FC<React.PropsWithChildren<Props>> = ({ loading, requests = [] }) => {
  // const { loading, data = [] } = useContext(RequestsContext)
  const { user } = useUser()
  const { userInfo } = useUserInfo()
  const active = requests.filter(isActiveRequest)
  if (loading || !user || !userInfo) return null
  // if (requests.length && !active.length) return <NoRequests />
  if (requests.length > 1) return null
  const created = fromNanoseconds(userInfo.created)
  const twoWeeks = addDays(created, 14)
  const mined = userInfo.xyoCollected ?? 0
  const steps = [
    {
      completed: Date.now() > twoWeeks.valueOf(),
      more: `Account ${user.email}, was created on ${created.toLocaleDateString()}.`,
      text: 'Account is at least 2 weeks old',
    },
    {
      completed: mined > 10000,
      more: `You have geomined ${Number(mined || 0).toLocaleString()}!`,
      text: 'Collected at least 10,000 COIN',
    },
    {
      completed: !!(userInfo.lastKnownAddress || userInfo.coinbaseEmail || userInfo.shipStreetAddress),
      more: <Typography dangerouslySetInnerHTML={{ __html: marked(createWalletDetails) }}></Typography>,
      text: 'Provided the necessary info',
    },
    {
      completed: !!active.length,
      more: () => <CreateRequestDetails steps={steps} />,
      text: 'Created a redeem request',
    },
  ]
  // const hasSteps = steps.some(({ completed }) => !completed)
  return (
    <CustomList title="Before You Redeem">
      {steps.map(({ completed, text, more }) => (
        <FlexCol key={text} alignItems="stretch">
          <Accordion>
            <AccordionSummary expandIcon={more && <ExpandMore />}>
              <FlexRow>
                <FlexRow marginRight={1}>{completed ? <FaCheck color="green" /> : <FaTimes color="red" />}</FlexRow>
                <Typography variant="body1">{text}</Typography>
              </FlexRow>
            </AccordionSummary>
            <AccordionDetails>{typeof more === 'function' ? more() : more}</AccordionDetails>
          </Accordion>
          <Divider />
        </FlexCol>
      ))}
    </CustomList>
  )
}

interface CreateRequestDetailsProps {
  steps: {
    completed?: boolean | null
    more: ReactNode | (() => ReactNode)
    text: ReactNode | (() => ReactNode)
  }[]
}

const CreateRequestDetails = ({ steps }: CreateRequestDetailsProps) => {
  const { user, onLogout } = useUser()
  return (
    <ol>
      <li>In the app, tap the assets bar where the total collected is displayed.</li>
      <li>Tap the option you are interested in, and then tap the &quot;Redeem&quot; button.</li>
      {steps && steps[0].completed && steps[1].completed ? (
        <li>
          If you submitted one through the app and are still seeing this message, please make sure that you are logged
          into the correct account. The email you are logged into here is <strong>{user && user.email}</strong>. In the
          app tap the top right account button and check to make sure you are using the same email. If not click{' '}
          <a className="text-danger cursor-pointer" onClick={() => onLogout?.()}>
            here
          </a>{' '}
          to log out and log back in with the correct email to enter your wallet address and get your redeem request
          processed.
        </li>
      ) : null}
    </ol>
  )
}
const createWalletDetails = `
  <p>If you'll be redeeming physical products you need to add your shipping information in the app by tapping <a href="https://my.coinapp.co/appLink/account">here</a> and the "Account Details" button.</p>
  <p>If you'll be redeeming for BTC or ETH you need to add your coinbase email in the app by tapping <a href="https://my.coinapp.co/appLink/account">here</a> and the "Account Details" button.</p>
  <p>If you'll be redeeming digital assets, you need to make sure you have an external wallet that can hold your assets. XYO Tokens are an ERC-20 Token, so if you want to hold XYO Tokens, then you will need to make sure your wallet is ERC-20 Compatible. We often suggest Trust Mobile Wallet or MetaMask Chrome extension for a new wallet that can hold ERC-20 Tokens. Trust is a smartphone app, and MetaMask can be used on the computer. We have extensive PDF instructions to assist you in making a wallet! Click the links below to view them!</p><p><strong><a href="https://cdn.xy.company/documents/MetaMask_Wallet_Instructions.pdf" target="_blank" rel="noopener">MetaMask Instructions</a></strong></p><p><strong><a href="https://cdn.xy.company/documents/Trust_Wallet_Instructions.pdf" target="_blank" rel="noopener">Trust Wallet Instructions</a></strong></p>`

export default BeforeYouWithdraw
